/**
 * PRD.218 — Patient Overview billing summary card.
 *
 * Shows lifetime Charged / Paid / Remaining for a patient using the
 * shared `usePatientBilling` hook so the numbers match the sidebar
 * balance chip exactly. Click takes the user to the Treatment Record
 * tab (same target as the sidebar chip) for full invoice context.
 */

import { NavIcon } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { usePatientBilling } from './usePatientBilling';

interface Props {
  patientId: string | null;
  /** Optional click target — typically navigates to the Treatment Record / Invoices tab. */
  onView?: () => void;
}

export function PatientBillingSummaryCard({ patientId, onView }: Props) {
  const billing = usePatientBilling(patientId);
  const fmt = useFormatMoney();

  const hasOutstanding = billing.balanceCents > 0;
  const isClear = billing.chargedCents > 0 && billing.balanceCents <= 0;

  const accent = hasOutstanding
    ? billing.hasOverdue
      ? 'var(--color-warning-text)'
      : 'var(--color-warning-text)'
    : isClear
      ? 'var(--color-success-text)'
      : 'var(--text-muted)';

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 'var(--sp-3)',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
            <NavIcon name="credit-card" />
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Billing
          </span>
        </div>
        {onView && (
          <button
            type="button"
            onClick={onView}
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-primary, #4f46e5)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            View invoices →
          </button>
        )}
      </div>

      {/* Stats row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 'var(--sp-3)',
        }}
      >
        <Stat label="Charged" value={fmt(billing.charged)} />
        <Stat
          label="Paid"
          value={fmt(billing.paid)}
          color={billing.paidCents > 0 ? 'var(--color-success-text)' : undefined}
        />
        <Stat label="Remaining" value={fmt(billing.balance)} color={accent} emphasised />
      </div>

      {/* Status hint */}
      {billing.chargedCents === 0 ? (
        <div
          style={{
            marginTop: 'var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          No invoices yet for this patient.
        </div>
      ) : isClear ? (
        <div
          style={{
            marginTop: 'var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-success-text)',
          }}
        >
          All invoices fully settled.
        </div>
      ) : (
        <div
          style={{
            marginTop: 'var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: billing.hasOverdue ? 'var(--color-warning-text)' : 'var(--text-muted)',
          }}
        >
          {billing.unpaidCount} unpaid invoice{billing.unpaidCount === 1 ? '' : 's'}
          {billing.hasOverdue ? ' · 30+ days overdue' : ''}
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  color,
  emphasised,
}: {
  label: string;
  value: string;
  color?: string;
  emphasised?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasised ? 'var(--font-size-base)' : 'var(--font-size-sm)',
          fontWeight: emphasised ? 700 : 600,
          color: color ?? 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}
